import { useState, useEffect } from 'react';
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap';
import { ProgressTracker } from '@/components/tools/ProgressTracker';
import { GroupedClientSelector } from '@/components/tools/GroupedClientSelector';
import { countOf, getSelectionDisplayText, reasonOf } from '@/components/tools/clientSelectionUtils';
import {
  IdCell,
  ReportChoices,
  ReportDownloads,
  ReportFailures,
  ReportStats,
  ReportTable,
  type ReportColumn,
} from '@/components/tools/ReportParts';
import { ToolPage } from '@/components/ui/ToolPage';
import { useToolExecution } from '@/hooks/useToolExecution';
import { AllUsersReportService } from '@/services/allUsersReportService';
import { clientService } from '@/services/clientService';
import { formatNzDate, formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import type { Client } from '@/types';
import type {
  AllUsersReportParameters,
  AllUsersReportResult,
  ToolResult,
  ToolResultFile,
  UserRecord,
} from '@/types/tools';

const USER_TYPE_LABEL: Record<AllUsersReportParameters['userTypeFilter'], string> = {
  all: 'All users',
  admin: 'Admins only',
  user: 'Standard users only',
};

/** What Cognito calls each account state, in words. An unknown state is shown as sent. */
const ACCOUNT_STATE: Record<string, string> = {
  CONFIRMED: 'Confirmed',
  UNCONFIRMED: 'Not confirmed',
  FORCE_CHANGE_PASSWORD: 'Must set a password',
  RESET_REQUIRED: 'Must reset the password',
  EXTERNAL_PROVIDER: 'Signs in with single sign-on',
  ARCHIVED: 'Archived',
  COMPROMISED: 'Compromised',
  UNKNOWN: 'Unknown',
  unknown: 'Unknown',
};

const USER_COLUMNS: ReportColumn<UserRecord>[] = [
  { key: 'client', header: 'Client', cell: (user) => user.clientName },
  { key: 'email', header: 'Email', cell: (user) => user.email },
  { key: 'username', header: 'Username', cell: (user) => <IdCell value={user.username} label="Copy username" /> },
  {
    key: 'role',
    header: 'Role',
    cell: (user) => (
      <Badge bg={user.userType === 'admin' ? 'info' : 'secondary'}>
        {user.userType === 'admin' ? 'Admin' : 'User'}
      </Badge>
    ),
  },
  { key: 'state', header: 'Account', cell: (user) => ACCOUNT_STATE[user.status] ?? user.status },
  {
    key: 'enabled',
    header: 'Can sign in',
    cell: (user) => <Badge bg={user.enabled ? 'success' : 'danger'}>{user.enabled ? 'Yes' : 'No'}</Badge>,
  },
  {
    key: 'lastLogin',
    header: 'Last sign-in',
    title: 'New Zealand time',
    cell: (user) =>
      user.lastLoginAt ? <span title={formatNzDateTime(user.lastLoginAt)}>{timeAgo(user.lastLoginAt)}</span> : null,
  },
  { key: 'created', header: 'Created', title: 'New Zealand time', cell: (user) => formatNzDate(user.createdAt) },
];

const DEFAULT_PARAMETERS: AllUsersReportParameters = {
  clientNames: [],
  userTypeFilter: 'all',
  outputFormat: 'csv',
  includeLastLogin: false,
};

export default function AllUsersReportTool() {
  const [parameters, setParameters] = useState<AllUsersReportParameters>(DEFAULT_PARAMETERS);
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [resultFiles, setResultFiles] = useState<ToolResultFile[]>([]);

  const { execution, isRunning, execute, cancel, reset } = useToolExecution({
    onCompleted: (result) => {
      if (result.files) {
        setResultFiles(result.files);
      }
    },
    onFailed: (error) => {
      console.error('Tool execution failed:', error);
    },
  });

  // Load clients on component mount
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoadingClients(true);
    setClientsError(null);
    try {
      const allClients = await clientService.getAllClients();
      setClients(allClients);
    } catch (error) {
      console.error('Failed to load clients:', error);
      setClientsError(reasonOf(error));
    } finally {
      setLoadingClients(false);
    }
  };

  const handleClientToggle = (clientName: string) => {
    setParameters((prev) => {
      const newClientNames = prev.clientNames.includes(clientName)
        ? prev.clientNames.filter((n) => n !== clientName)
        : [...prev.clientNames, clientName];
      return { ...prev, clientNames: newClientNames };
    });
  };

  const handleSelectClients = (clientNames: string[]) => {
    setParameters((prev) => ({ ...prev, clientNames }));
  };

  const handleExecute = async () => {
    // If all clients are selected, pass empty array to indicate "all clients"
    const clientNamesToUse = parameters.clientNames.length === clients.length ? [] : parameters.clientNames;
    const paramsToUse = { ...parameters, clientNames: clientNamesToUse };

    await execute('all-users-report', paramsToUse as unknown as Record<string, unknown>, async (params, onProgress) => {
      const { result, files } = await AllUsersReportService.generateReport(
        params as unknown as AllUsersReportParameters,
        onProgress
      );

      return {
        type: 'file',
        files,
        data: result,
      } as ToolResult;
    });
  };

  /** Back to the form with the same choices. */
  const handleChangeChoices = () => {
    reset();
    setResultFiles([]);
  };

  /** Back to the form with every choice reset. */
  const handleReset = () => {
    reset();
    setResultFiles([]);
    setParameters(DEFAULT_PARAMETERS);
  };

  const isFormValid = () => {
    return parameters.clientNames.length > 0 && parameters.outputFormat;
  };

  const report = execution?.result?.data as AllUsersReportResult | undefined;
  const finished = execution?.status === 'completed';
  const users = report?.users ?? [];
  const failedClients = report?.failedClients ?? [];

  return (
    <ToolPage title="All users report" subtitle="List every user on one client or on all of them, with their role.">
      {clientsError && (
        <Alert variant="danger">
          The client list did not load, so there is nothing to pick from. {clientsError}{' '}
          <Button variant="link" className="report-inline-link" onClick={loadClients}>
            Try again
          </Button>
        </Alert>
      )}

      {!execution && (
        <Card className="report-form">
          <Card.Body>
            <div className="report-form__grid">
              <div className="report-form__col">
                <h2 className="section-label">Clients</h2>
                <GroupedClientSelector
                  clients={clients}
                  selectedClientNames={parameters.clientNames}
                  onClientToggle={handleClientToggle}
                  onSelectClients={handleSelectClients}
                  disabled={isRunning}
                  loading={loadingClients}
                />
              </div>

              <div className="report-form__col">
                <h2 className="section-label">Options</h2>
                <Form.Group className="report-field" controlId="all-users-type">
                  <Form.Label>Users to list</Form.Label>
                  <Form.Select
                    value={parameters.userTypeFilter}
                    onChange={(e) =>
                      setParameters((prev) => ({
                        ...prev,
                        userTypeFilter: e.target.value as 'all' | 'admin' | 'user',
                      }))
                    }
                    disabled={isRunning}
                  >
                    <option value="all">{USER_TYPE_LABEL.all}</option>
                    <option value="admin">{USER_TYPE_LABEL.admin}</option>
                    <option value="user">{USER_TYPE_LABEL.user}</option>
                  </Form.Select>
                  <Form.Text>An admin is a person in the workspace&apos;s admin group.</Form.Text>
                </Form.Group>

                <Form.Group className="report-field">
                  <Form.Check
                    type="switch"
                    id="include-last-login"
                    label="Add each user's last sign-in"
                    checked={parameters.includeLastLogin}
                    onChange={(e) =>
                      setParameters((prev) => ({
                        ...prev,
                        includeLastLogin: e.target.checked,
                      }))
                    }
                    disabled={isRunning}
                  />
                  <Form.Text>The report makes one more read for every user, so it takes longer.</Form.Text>
                </Form.Group>

                <Form.Group className="report-field" controlId="all-users-format">
                  <Form.Label>File format</Form.Label>
                  <Form.Select
                    value={parameters.outputFormat}
                    onChange={(e) =>
                      setParameters((prev) => ({
                        ...prev,
                        outputFormat: e.target.value as 'csv' | 'json',
                      }))
                    }
                    disabled={isRunning}
                  >
                    <option value="csv">CSV</option>
                    <option value="json">JSON</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </Card.Body>
          <div className="report-form__foot">
            <p className="report-form__hint">
              {parameters.clientNames.length === 0
                ? 'Pick at least one client.'
                : `Reads ${countOf(parameters.clientNames.length, 'client account')}, one after the other. More clients take longer.`}
            </p>
            <Button variant="primary" onClick={handleExecute} disabled={!isFormValid() || isRunning || loadingClients}>
              Run report
            </Button>
          </div>
        </Card>
      )}

      {execution && (
        <Card className="report-run">
          <Card.Body>
            <ReportChoices
              running={isRunning}
              onChange={handleChangeChoices}
              onStartOver={handleReset}
              choices={[
                { label: 'Clients', value: getSelectionDisplayText(clients, parameters.clientNames) },
                { label: 'Users', value: USER_TYPE_LABEL[parameters.userTypeFilter] },
                { label: 'Last sign-in', value: parameters.includeLastLogin ? 'Included' : 'Left out' },
                { label: 'File format', value: parameters.outputFormat.toUpperCase() },
              ]}
            />
            <div className="report-run__progress">
              <ProgressTracker
                status={execution.status}
                progress={execution.progress}
                error={execution.error}
                startedAt={execution.startedAt}
                completedAt={execution.completedAt}
                onStop={cancel}
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {finished && report && (
        <>
          <p className="report-result__note">
            {countOf(report.metadata.totalUsers, 'user')} on {countOf(report.metadata.clientsProcessed, 'client')}, read{' '}
            {formatNzDateTime(report.metadata.exportDate)} New Zealand time.
          </p>

          <ReportStats
            stats={[
              { label: 'Users', value: report.metadata.totalUsers.toLocaleString() },
              { label: 'Admins', value: report.metadata.adminUsers.toLocaleString() },
              { label: 'Standard users', value: report.metadata.regularUsers.toLocaleString() },
              { label: 'Clients read', value: report.metadata.clientsProcessed.toLocaleString() },
              ...(report.metadata.clientsFailed > 0
                ? [{ label: 'Clients not read', value: report.metadata.clientsFailed.toLocaleString() }]
                : []),
            ]}
          />

          <ReportFailures failures={failedClients} />

          {resultFiles.length === 0 && (
            <Alert variant="warning">
              {report.message || 'No users were found on the chosen clients.'} There is no file to download. Change the
              choices to try other clients or a wider set of users.
            </Alert>
          )}

          {users.length > 0 && (
            <Card className="report-card">
              <Card.Header className="report-card__head">
                <h2 className="report-card__title">
                  Users
                  <span className="report-card__sub">{countOf(users.length, 'row')}</span>
                </h2>
                <ReportDownloads files={resultFiles} />
              </Card.Header>
              <ReportTable columns={USER_COLUMNS} rows={users} empty="No users to show." />
            </Card>
          )}
        </>
      )}
    </ToolPage>
  );
}
